// Shared voice chrome — ONE call anatomy for every posture a voice session
// takes: the in-composer strip (VoiceStrip), the bottom-centre island
// (SimoneReasoningPill's voice case) and the expanded call screen
// (VoiceOverlay). Each host composes these pieces; none grows its own set.
//
// THE CALL (Nika 2026-10-22: "improve design and UX of voice calling mode in
// workspace, both in bottom bar and when its expanded"). The reference is the
// Fly call she approved for shaker on 2026-10-18 — an orb, live captions, mute,
// end, a clock. Before this the three postures each drew the session their own
// way: a 13-bar dash wave and a centred engine tag in the island, a bare status
// line in the strip, a waveform with no mute and no way back to the bar on the
// full screen (its only exit ended the session). Now:
//
//   • VoiceOrb         — the assistant's presence: an ink disc carrying the state
//                        glyph, with a ring that follows the mic level while it
//                        listens and pulses while it speaks
//   • VoiceCallStatus  — the lifecycle word and the call clock ("Listening 0:42")
//   • VoiceCallCaption — the newest line of the conversation, one line (bars)
//   • VoiceControls    — the bar controls, one set and one order in both bars:
//                        [Try again | Stop] [Type] [Mute] [Expand] [End call]
//   • VoiceCallActions — the expanded screen's labelled trio: Type · End · Mute
//
// Every control is state-derived, never host-derived: the same component
// behaves correctly on a composer strip (voiceHost set) and on the island
// (voiceHost null) without either host configuring it. The 60 fps half (the
// orb's ring, the clock's tick) is `voiceCallFrame` (functions/voiceSession.js),
// run from the onFrame of whichever host is on show.

// Which engine is actually running. Deliberately quiet — a dot plus a word,
// at caption weight — but present, so a live agent conversation and the
// one-shot pipeline never look identical (2026-08-02 per Nika "not sure if
// elevenlabs agent is speaking"). Absent when no session is live. The call
// surfaces now say it in the expanded header (VoiceCallIdentity); this tag
// stays registered for the UI kit's specimen.
export const VoiceEngineTag = {
  // FA204 / frank trap — `_engineWhy` used to be a bare module-scope function
  // read from inside `title` (a serialized factory); it silently read as
  // undefined once this component is frank.toJSON'd for platform delivery
  // (workspace d6ea1142, project_frank_drops_module_local_helpers). Moved
  // into `scope`, called via `el.scope.engineWhy`.
  scope: {
    // Why the live engine is the one it is. Talk-only
    // (VOICE-TALK-ONLY-REMOVE-DEAD-CONVAI-UI-1): only the live SIMONE/command
    // split remains. Returns a bundle KEY; the `title:` call site resolves it
    // (an attribute takes the FUNCTION form, I18N-CONVENTION §1).
    engineWhy(root) {
      const r = root || {}
      if (r.voiceMode !== 'talk') return 'shell.voice.engine.whyCommand'
      return 'shell.voice.engine.whyTalk'
    }
  },
  display: null,
  // FA515 — `voiceOpen && voiceEngine` short-circuits: any run where
  // voiceOpen is falsy never reads voiceEngine at all. Declarative deps make
  // the subscription independent of control flow (workspace ff9de771).
  showDeps: ['voiceOpen', 'voiceEngine'],
  show: (el, s) => !!(s.root && s.root.voiceOpen && s.root.voiceEngine),
  flow: 'x',
  align: 'center center',
  gap: 'Y',
  flexShrink: '0',
  // Say WHY, not just what — the tooltip carries the full sentence.
  title: (el, s) => el.call('polyglot', el.scope.engineWhy(s && s.root)),
  Dot: {
    width: 'Y',
    height: 'Y',
    round: 'radiusPill',
    flexShrink: '0',
    backgroundDeps: ['voiceEngine'],
    // Talk is Simone on both talk engines: the live agent and the loop.
    background: (el, s) => {
      const engine = s && s.root && s.root.voiceEngine
      return engine === 'simone' || engine === 'agent' ? 'title' : 'caption'
    }
  },
  Label: {
    tag: 'span',
    fontSize: 'Y',
    fontWeight: '700',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: 'caption',
    whiteSpace: 'nowrap',
    textDeps: ['voiceEngine'],
    // `Simone` is the agent's own NAME and stays Latin (glossary rule 4).
    text: (el, s) => {
      const engine = s && s.root && s.root.voiceEngine
      return engine === 'simone' || engine === 'agent'
        ? 'Simone'
        : el.call('polyglot', 'shell.voice.engine.voice')
    }
  }
}

// THE ORB — what the member is talking to. An ink disc (the solid-ink
// treatment, inverted in both schemes: `title` fill, `document` glyph) whose
// glyph says the state at a glance, over a ring the frame loop scales: with
// the live mic level while it listens, a slow pulse while the reply plays, a
// breath while it thinks. Muted outranks the lifecycle — a muted mic is the
// one state the member must never miss. Decorative for assistive tech: the
// status line beside it carries the same thing in words.
//
// Size comes from the host (`boxSize`): the bars use B2, the control height,
// so the orb and the buttons share one row height; the call screen uses D1.
export const VoiceOrb = {
  scope: {
    orbGlyph(root) {
      const r = root || {}
      if (r.voiceStatus === 'error') return 'warning'
      if (r.voiceMicMuted) return 'micOff'
      if (r.voiceStatus === 'speaking') return 'wave'
      if (r.voiceStatus === 'thinking') return 'magicstar'
      // The loop holds: a step waits for the person's OK on the screen, or a
      // question for their pick.
      if (r.voiceStatus === 'waiting' || r.voiceStatus === 'choosing') return 'clock'
      if (r.voiceStatus === 'done') return 'checkmark'
      return 'mic'
    }
  },
  aria: { hidden: 'true' },
  position: 'relative',
  flexShrink: '0',
  boxSize: 'B2',

  // Scaled per frame by voiceCallFrame (setNodeStyles — never state). It
  // starts flush with the disc, so a host whose frame loop is not running
  // (a hidden tab) still shows a clean disc.
  VoiceOrbRing: {
    position: 'absolute',
    inset: '0',
    round: '100%',
    background: 'title',
    opacity: '0.14',
    pointerEvents: 'none',
    transformOrigin: 'center center',
    transform: 'scale(1)'
  },

  VoiceOrbCore: {
    position: 'relative',
    boxSize: '100%',
    round: '100%',
    flow: 'x',
    align: 'center center',
    background: 'title',
    color: 'document',
    transition: 'B background defaultBezier, B color defaultBezier',
    // An error is a real status: the error fill and the ink the brand measured
    // against it (errorFillBg / errorFillInk — the `error` theme's own pair).
    isError: (el, s) => (s.root && s.root.voiceStatus) === 'error',
    '.isError': { background: 'errorFillBg', color: 'errorFillInk' },
    Icon: {
      name: (el, s) => el.scope.orbGlyph(s.root),
      boxSize: '45%'
    }
  }
}

// The call clock — m:ss from `root.voiceStartedAt` (h:mm:ss past an hour).
// Its seconds live in the clock's OWN state: voiceCallFrame writes them once a
// second from the host's frame loop, so the tick re-renders this span and
// nothing else (a root write every second would wake the whole shell).
export const VoiceCallClock = {
  tag: 'span',
  state: { secs: 0 },
  scope: {
    clockText(secs) {
      const t = Math.max(0, Math.floor(Number(secs) || 0))
      const h = Math.floor(t / 3600)
      const m = Math.floor((t % 3600) / 60)
      const ss = String(t % 60).padStart(2, '0')
      return h ? h + ':' + String(m).padStart(2, '0') + ':' + ss : m + ':' + ss
    }
  },
  display: null,
  // Function deps: a string dep reads `s[key]`, and `s` is this clock's own
  // state — the session keys live on the root.
  showDeps: [(el, s) => s.root && s.root.voiceOpen, (el, s) => s.root && s.root.voiceStartedAt],
  show: (el, s) => !!(s.root && s.root.voiceOpen && s.root.voiceStartedAt),
  flexShrink: '0',
  fontSize: 'Z',
  lineHeight: '1.2',
  color: 'caption',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  title: (el) => el.call('polyglot', 'shell.voice.call.duration'),
  text: (el, s) => el.scope.clockText(s.secs)
}

// The lifecycle word and the clock. The word is the SHORT call form ("Listening",
// "Muted", "Thinking…"); an error shows the error itself (cut to one line, the
// tooltip holds the whole of it — the overflow law). A polite live region: the
// member hears the state change without the transcript being re-read.
export const VoiceCallStatus = {
  scope: {
    // Returns DISPLAY TEXT — the error copy is already resolved when it lands
    // in root state (VoiceOverlay.scope.fail), the rest are bundle keys.
    callStatusText(el, root) {
      const r = root || {}
      const t = (k) => el.call('polyglot', k)
      if (r.voiceStatus === 'error') return r.voiceError || t('shell.voice.status.error')
      // The live agent's call is still connecting (VoiceOverlay.scope.startAgentEngine).
      if (r.voiceStatus === 'connecting') return t('shell.voice.status.connecting')
      // The loop holds for the person (VoiceOverlay.scope.holdForAsk): the
      // thread's own words — a step that waits for the OK (ChatMessage, the
      // island), a question that waits for a pick (the ask_user row).
      if (r.voiceStatus === 'waiting') return t('ai.chat.needsOk')
      if (r.voiceStatus === 'choosing') return t('ai.toolStep.choose')
      if (r.voiceMicMuted && r.voiceStatus !== 'speaking') return t('shell.voice.call.muted')
      if (r.voiceStatus === 'thinking') return t('shell.voice.status.thinking')
      if (r.voiceStatus === 'speaking') return t('shell.voice.status.speaking')
      if (r.voiceStatus === 'done') return '✓ ' + t('shell.voice.status.done')
      return t(
        r.voiceMode === 'talk' ? 'shell.voice.call.listening' : 'shell.voice.status.listening'
      )
    }
  },
  flow: 'x',
  // Baseline, not centre: the word and the smaller clock read as one line.
  align: 'baseline flex-start',
  gap: 'Y1',
  minWidth: '0',
  role: 'status',
  aria: { live: 'polite' },

  VoiceCallWord: {
    tag: 'span',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 'Z1',
    fontWeight: '500',
    // The bar's two lines fit the 36px control height (see VoiceCallInfo).
    lineHeight: '1.2',
    color: (el, s) => ((s.root && s.root.voiceStatus) === 'error' ? 'dangerInk' : 'title'),
    text: (el, s) => el.scope.callStatusText(el, s.root),
    title: (el, s) => el.scope.callStatusText(el, s.root)
  },

  VoiceCallClock: {}
}

// The newest line of the conversation, on one line — what a bar has room for.
// Before the first turn it is the session's hint (talk: "Speak naturally …";
// the one-shot command: the command examples). The bars show their lines as
// bubbles above them (AiBubbleStack); the call screen's captions are VoiceLog.
export const VoiceCallCaption = {
  scope: {
    // Mirrors VoiceLog's source precedence (voiceTurns, then the assistant
    // thread) — the newest non-empty row only.
    latestTurn(root) {
      const live = (root && root.voiceTurns) || []
      for (let i = live.length - 1; i >= 0; i--) {
        const t = live[i]
        if (t && String(t.text || '').trim()) {
          const role = t.kind === 'action' ? 'action' : t.role === 'user' ? 'user' : 'agent'
          return { role, text: String(t.text).trim() }
        }
      }
      return null
    },
    captionText(el, root) {
      const r = root || {}
      const turn = this.latestTurn(r)
      if (!turn) {
        return el.call(
          'polyglot',
          r.voiceMode === 'talk' ? 'shell.voice.call.speakNaturally' : 'shell.voice.hint.command'
        )
      }
      const who =
        turn.role === 'user'
          ? 'shell.voice.log.you'
          : turn.role === 'action'
            ? 'shell.voice.log.done'
            : 'shell.voice.log.assistant'
      return el.call('polyglot', who) + ': ' + turn.text.replace(/\s+/g, ' ')
    }
  },
  tag: 'span',
  minWidth: '0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 'Z',
  lineHeight: '1.2',
  color: 'caption',
  text: (el, s) => el.scope.captionText(el, s.root),
  title: (el, s) => el.scope.captionText(el, s.root)
}

// THE BAR ROW — orb · status and caption · controls. The composer strip mounts
// it whole; the island builds the same row into its constant head. Both bars
// float the call's lines ABOVE them as bubbles (AiBubbleStack), so the row's
// caption is the hint before anyone has spoken and then steps aside — the
// newest line would only repeat the bubble right above it. A strip that cannot
// float them (the drawer's, `floatWords: false`) keeps the newest line here.
export const VoiceCallRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'Z',
  width: '100%',
  minWidth: '0',

  VoiceOrb: {},

  // Two tight lines that stay inside the 36px control height, so the row is
  // exactly as tall as the orb and the buttons — and on the composer's
  // capsule the strip's inset then keeps the orb and the hang-up concentric
  // with its round ends.
  VoiceCallInfo: {
    flow: 'y',
    gap: '0',
    minWidth: '0',
    flex: '1 1 auto',
    VoiceCallStatus: {},
    // The hint before anyone has spoken; then the bubbles above carry the
    // lines — unless this strip cannot float them (VoiceStrip floatWords).
    VoiceCallCaption: {
      hide: (el, s) => {
        const strip = typeof el.lookup === 'function' ? el.lookup('VoiceStrip') : null
        return !!strip && strip.floatWords !== false && !!el.scope.latestTurn(s.root)
      }
    }
  },

  VoiceControls: {}
}

// The bar controls. Icon-only (the bars are composer-width) and on
// NavbarButton so every one carries the house tooltip through its title, at
// the composer's own 36px circle. One set, one order, in both bars:
//
//   [Try again | Stop speaking]  [Type]  [Mute]  [Expand]  [End call]
//
// Type shows only while a composer hosts (the island sits right above a
// visible composer, so there it would only repeat a click on the field), and
// Expand only in a bar (the call screen has its own Minimize).
export const VoiceControls = {
  // FA204 / frank trap — every helper lives in `scope`, called via
  // `el.scope.<name>` (project_frank_drops_module_local_helpers).
  scope: {
    // Type = "give me the text input back". While a composer HOSTS a live talk
    // session that means handing the conversation to the island (the session
    // survives, only the surface owner changes) and putting the caret in the
    // field the strip gave back; the one-shot command and an errored session
    // have nothing to hand off, so it ends voice.
    backToTyping(el, s) {
      const r = s && s.root
      if (!r || !r.update) return
      if (r.voiceHost && r.voiceMode === 'talk' && r.voiceStatus !== 'error') {
        r.update({ voiceMinimized: true, voiceHost: null }, { preventFetch: true })
        // After the write: the field's `hide` re-runs in the update's
        // microtask, and the focus is queued behind it (the strip stays in
        // the tree while hidden, so the field is still found through it).
        el.call('voiceFocusComposer')
      } else {
        el.call('voiceStop')
      }
    }
  },
  flow: 'x',
  align: 'center center',
  gap: 'Y',
  flexShrink: '0',
  role: 'group',
  aria: { label: (el) => el.call('polyglot', 'shell.voice.call.controls') },
  // Every control is a NavbarButton with a semantically-named key — the
  // shared base rides on childExtends, and the names stay readable for every
  // reference to "KeyboardBtn", "the mute button" etc. in the file family.
  childExtends: 'NavbarButton',

  // `error` must never be a dead end: nothing re-arms from it on its own. A
  // registered `voiceRetry` re-runs the engine switch; without it, stop and
  // open a fresh session in the same mode (which also re-primes the reply
  // audio inside this click).
  RetryBtn: {
    display: null,
    showDeps: ['voiceStatus'],
    show: (el, s) => (s.root && s.root.voiceStatus) === 'error',
    icon: 'refresh',
    title: (el) => el.call('polyglot', 'shell.voice.retry'),
    flexShrink: '0',
    onClick: (e, el, s) => {
      e.stopPropagation()
      const fns = (el.context && el.context.functions) || {}
      if (typeof fns.voiceRetry === 'function') {
        el.call('voiceRetry')
        return
      }
      const r = s && s.root
      el.call('voiceStop')
      el.call('voiceStart', { mode: r && r.voiceMode === 'talk' ? 'talk' : 'command' })
    }
  },

  // Halts the spoken reply WITHOUT ending the session — the talk loop goes
  // straight back to listening (the barge-in).
  StopBtn: {
    display: null,
    showDeps: ['voiceStatus'],
    show: (el, s) => (s.root && s.root.voiceStatus) === 'speaking',
    icon: 'stop',
    title: (el) => el.call('polyglot', 'shell.voice.stopSpeaking'),
    flexShrink: '0',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceStopSpeaking')
    }
  },

  // Not on a phone: the composer row has no room for five controls beside the
  // status (it cut "Speaking…" to "S…" at 390px), the call hands itself to the
  // island after the first line anyway, and the call screen keeps Type.
  KeyboardBtn: {
    display: null,
    showDeps: ['voiceOpen', 'voiceHost', '_isPhoneViewport'],
    show: (el, s) =>
      !!(s.root && s.root.voiceOpen && s.root.voiceHost && s.root._isPhoneViewport !== true),
    icon: 'keyboard',
    title: (el) => el.call('polyglot', 'shell.voice.backToTyping'),
    flexShrink: '0',
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.scope.backToTyping(el, s)
    }
  },

  // INPUT-axis mute: never ends the session, never changes posture. A toggle
  // with a FIXED name — aria-pressed says the state (the WorkspaceCallPanel
  // rule); the tooltip says what a click does. Pressed = the danger ink on its
  // own wash, so a muted mic reads from across the room (the meet dock's OFF
  // plate).
  MicBtn: {
    icon: (el, s) => (s.root && s.root.voiceMicMuted ? 'micOff' : 'mic'),
    'aria-label': (el) => el.call('polyglot', 'shell.voice.mic.mute'),
    'aria-pressed': (el, s) => String(!!(s.root && s.root.voiceMicMuted)),
    title: (el, s) =>
      el.call(
        'polyglot',
        s.root && s.root.voiceMicMuted ? 'shell.voice.mic.unmute' : 'shell.voice.mic.mute'
      ),
    flexShrink: '0',
    isMuted: (el, s) => !!(s.root && s.root.voiceMicMuted),
    '.isMuted': {
      color: 'dangerInk',
      background: 'color-mix(in srgb, currentColor 14%, transparent)'
    },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceMicToggle')
    }
  },

  // A bar opens the call screen. From the composer strip too: voiceExpand
  // releases the hosting composer, so the screen (which shows only while no
  // composer hosts) can take over.
  ExpandBtn: {
    display: null,
    showDeps: ['voiceOpen', 'voiceMinimized', 'voiceHost'],
    show: (el, s) => !!(s.root && s.root.voiceOpen && (s.root.voiceMinimized || s.root.voiceHost)),
    icon: 'fullscreen',
    title: (el) => el.call('polyglot', 'shell.voice.expand'),
    flexShrink: '0',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceExpand')
    }
  },

  // End call — the house hang-up (WorkspaceCallPanel's HangUp): the `error`
  // status fill with its own measured ink, the handset turned down.
  EndBtn: {
    theme: 'error',
    icon: 'phone',
    title: (el) => el.call('polyglot', 'shell.voice.call.end'),
    flexShrink: '0',
    Icon: { transform: 'rotate(135deg)' },
    ':hover': { filter: 'brightness(1.08)' },
    ':active': { transform: 'scale(0.94)' },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceStop')
    }
  }
}

// One labelled call control of the call screen — a disc over its word, one
// button (the word is part of the hit area, which matters on a phone). The
// instance names its glyph and word (`VoiceCallDisc.Icon`, `VoiceCallLabel`).
// The call screen is pinned dark (VoiceOverlay `themeModifier: 'dark'`), so
// the disc's wash is a white tint by design, not a theme-invariant mistake.
export const VoiceCallAction = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  align: 'center center',
  gap: 'Y1',
  minWidth: 'D',
  padding: 'Y',
  round: 'radiusCard',
  background: 'transparent',
  borderWidth: '0',
  color: 'title',
  cursor: 'pointer',
  transition: 'B filter defaultBezier, B transform defaultBezier',
  // Hover brightens the rendered fill (the disc's wash or the hang-up plate)
  // and the word with it — never an opacity fade (Rule 65).
  ':hover': { filter: 'brightness(1.25)' },
  ':active': { transform: 'scale(0.96)' },

  VoiceCallDisc: {
    boxSize: 'C1',
    round: '100%',
    flow: 'x',
    align: 'center center',
    background: 'white.12',
    transition: 'B background defaultBezier, B color defaultBezier',
    Icon: { boxSize: 'A1' }
  },

  VoiceCallLabel: {
    tag: 'span',
    fontSize: 'Z',
    fontWeight: '500',
    color: 'caption',
    whiteSpace: 'nowrap'
  }
}

// The call screen's trio — Type · End · Mute, End in the middle and the
// largest-signal colour (the Fly call's order). Type minimizes the call to the
// island and puts the caret in the composer; End ends; Mute toggles the mic
// with the same fixed name + aria-pressed as the bar's MicBtn.
export const VoiceCallActions = {
  role: 'group',
  aria: { label: (el) => el.call('polyglot', 'shell.voice.call.controls') },
  flow: 'x',
  align: 'flex-start center',
  gap: 'A',
  childExtends: 'VoiceCallAction',

  TypeAction: {
    'aria-label': (el) => el.call('polyglot', 'shell.voice.call.typeInstead'),
    title: (el) => el.call('polyglot', 'shell.voice.call.typeInstead'),
    VoiceCallDisc: { Icon: { name: 'keyboard' } },
    VoiceCallLabel: { text: '{{ shell.voice.call.type | polyglot }}' },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceMinimize')
      el.call('voiceFocusComposer')
    }
  },

  EndAction: {
    'aria-label': (el) => el.call('polyglot', 'shell.voice.call.end'),
    title: (el) => el.call('polyglot', 'shell.voice.call.end'),
    // The hang-up plate: the error fill and the ink the brand measured on it
    // (the `error` theme's own pair, set as props — a theme here would lose
    // to the disc's own wash).
    VoiceCallDisc: {
      background: 'errorFillBg',
      color: 'errorFillInk',
      Icon: { name: 'phone', transform: 'rotate(135deg)' }
    },
    VoiceCallLabel: { text: '{{ shell.voice.call.endShort | polyglot }}' },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceStop')
    }
  },

  MuteAction: {
    'aria-label': (el) => el.call('polyglot', 'shell.voice.mic.mute'),
    'aria-pressed': (el, s) => String(!!(s.root && s.root.voiceMicMuted)),
    title: (el, s) =>
      el.call(
        'polyglot',
        s.root && s.root.voiceMicMuted ? 'shell.voice.mic.unmute' : 'shell.voice.mic.mute'
      ),
    VoiceCallDisc: {
      // Pressed = the bar MicBtn's own treatment: the danger ink on its wash.
      isMuted: (el, s) => !!(s.root && s.root.voiceMicMuted),
      '.isMuted': {
        color: 'dangerInk',
        background: 'color-mix(in srgb, currentColor 18%, transparent)'
      },
      Icon: { name: (el, s) => (s.root && s.root.voiceMicMuted ? 'micOff' : 'mic') }
    },
    VoiceCallLabel: { text: '{{ shell.voice.call.mute | polyglot }}' },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('voiceMicToggle')
    }
  }
}
